import { BsPrinter } from "react-icons/bs";
import { FiEdit } from "react-icons/fi";
import { HiXMark } from "react-icons/hi2";
import { RiDeleteBin6Line } from "react-icons/ri";
import { toast } from "react-toastify";
import { PrintLayout } from "../PrintLayout";

const ViewDeathRecordModal = ({ isOpen, onClose, record, onEdit, onDelete }) => {
  if (!isOpen || !record) return null;

   const handlePrintBill = () => {
    // Hide all elements except the print layout
    document.body.style.visibility = 'hidden';
    const printLayout = document.querySelector('.print-death-record');
    if (printLayout) {
      printLayout.style.visibility = 'hidden';
      printLayout.style.display = 'block';
    }

    window.print();

    // Restore visibility after printing
    setTimeout(() => {
      document.body.style.visibility = '';
      if (printLayout) {
        printLayout.style.display = 'none';
      }
    }, 1000);
  };

  const handleEdit = () => {
    onEdit(record);
    onClose();
  };

  const handleDelete = () => {
    onDelete(record.id);
    onClose();
  };

  const fields = [
    { label: "Record ID", value: record.id || "N/A" },
    { label: "Patient Name", value: record.patientName || "N/A" },
    {
      label: "Death Date",
      value: record.deathDate ? new Date(record.deathDate).toLocaleDateString() : "N/A",
    },
    { label: "Guardian Name", value: record.guardianName || "N/A" },
    {
      label: "Attachment",
      value: record.attachment ? (
        <img
          src={`https://api.muktihospital.com/uploads/${record.attachment}`}
          alt="Attachment"
          className="w-16 h-16 object-cover rounded-md border border-gray-300 shadow-sm"
          onError={(e) => {
            e.target.src = "/path/to/placeholder-image.jpg"; // Replace with actual placeholder
          }}
        />
      ) : "N/A",
    },
    { label: "Report", value: record.report || "N/A" },
  ];

  // Define button configurations with custom styles
  const buttons = [
    {
      icon: <BsPrinter size={16} />,
      onClick: handlePrintBill,
      title: "Print Record",
      className:
        "size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300",
    },
    {
      icon: <FiEdit size={16} />,
      onClick: handleEdit,
      title: "Edit",
      className:
        "size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300",
    },
    {
      icon: <RiDeleteBin6Line size={16} />,
      onClick: handleDelete,
      title: "Delete",
      className:
        "size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300",
    },
    {
      icon: <HiXMark size={16} />,
      onClick: () => {
        toast.dismiss();
        onClose();
      },
      title: "Close",
      className:
        "size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300",
    },
  ];

  // Prepare data for PrintLayout
  const billInfo = (
    <>
      <div>
        <p><strong>Record ID:</strong> {record.id || "N/A"}</p>
        <p><strong>Patient Name:</strong> {record.patientName || "N/A"}</p>
        <p><strong>Death Date:</strong> {record.deathDate ? new Date(record.deathDate).toLocaleDateString() : "N/A"}</p>
      </div>
      <div>
        <p><strong>Guardian Name:</strong> {record.guardianName || "N/A"}</p>
        <p><strong>Attachment:</strong> {record.attachment ? "Available" : "N/A"}</p>
      </div>
    </>
  );

  const note = (
    <>
      <div className="note-box"><strong>Report:</strong> {record.report || "N/A"}</div>
    </>
  );

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 print-hidden">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-[95%] sm:max-w-[90%] md:max-w-[1000px] max-h-[90vh] overflow-y-auto print-hidden">
          {/* Modal Header */}
          <div className="flex items-center justify-between gap-3 flex-wrap border-b border-dashed border-gray-300 p-4 print-hidden">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">View Death Record</h2>
            <div className="flex items-center gap-2 flex-wrap">
              {buttons.map((btn, index) => (
                <button
                  key={index}
                  onClick={btn.onClick}
                  className={`${btn.className}`}
                  title={btn.title}
                >
                  {btn.icon}
                </button>
              ))}
            </div>
          </div>
          {/* Modal Body */}
          <div className="p-4 sm:p-6 print-hidden">
            {/* Table for Record Details */}
            <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm print-hidden">
              <table className="min-w-full text-sm divide-y divide-gray-200 print-hidden">
                <tbody className="bg-white divide-y divide-gray-200 print-hidden">
                  {fields.map((field, index) => (
                    <tr
                      key={index}
                      className={`hover:bg-blue-50 transition-colors duration-200 print-hidden ${
                        index % 2 === 0 ? "bg-gray-50/50" : "bg-white"
                      }`}
                    >
                      <th className="px-4 py-3 text-left font-bold text-gray-700 text-xs sm:text-sm w-[40%] sm:w-[30%]">
                        {field.label}
                      </th>
                      <td className="px-4 py-3 text-gray-900 font-inter text-xs sm:text-sm break-words">
                        {field.value}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Print Layout */}
      <PrintLayout
        className="print-death-record"
        title="Death Record"
        billInfo={billInfo}
        note={note}
      />
    </>
  );
};

export default ViewDeathRecordModal;